import type { FieldPolicy } from '@apollo/client/cache/inmemory/policies';
import type { Query } from 'gql/graphql';
import React, { useMemo } from 'react';
import {
    ApolloClient,
    ApolloProvider,
    InMemoryCache,
    createHttpLink,
    NormalizedCacheObject,
    type Reference,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import useGetToken from '../util/useGetToken';

declare global {
    interface Window {
        apolloClient: ApolloClient<NormalizedCacheObject>
    }
}

type Props = {
    children: React.ReactNode
}

type KeysEnum<T> = { [P in keyof Required<T>]: string };

const topTypes: Omit<KeysEnum<Query>,
    'audioConnection' |
    'audioGroupsConnection' |
    'charactersConnection' |
    'compsConnection' |
    'contextBlocksConnection' |
    'episodeContents' |
    'episodesConnection' |
    'lineGroupsConnection' |
    'linesConnection' |
    'recordingSessionsConnection' |
    'scriptContents' |
    'scriptsConnection' |
    'seasonsConnection' |
    'showsConnection' |
    'takesConnection' |
    'draftRecordingSessionLines' |
    'productionData' |
    'parsedLinesOutputs' |
    'parsedLinesOutputUnions' |
    '__typename'
> = {
    audio: 'Audio',
    audioGroups: 'AudioGroup',
    characters: 'Character',
    comps: 'Comp',
    contextBlocks: 'ContextBlock',
    episodes: 'Episode',
    lineGroups: 'LineGroup',
    lines: 'Line',
    recordingSessions: 'RecordingSession',
    scripts: 'Script',
    seasons: 'Season',
    shows: 'Show',
    takes: 'Take',
    appVersion: 'ignore',
};

// This tells Apollo how to check the cache for existing data when querying by ID
// https://www.apollographql.com/docs/react/caching/advanced-topics/#cache-redirects
const fields = Object.entries(topTypes).reduce((acc, curr) => {
    if (curr[1] === 'ignore') return acc;

    acc[curr[0]] = {
        read: (existing, { args, toReference, canRead }) =>
        {
            if (existing) return existing; // Things get fucky if you remove this. Basically let Apollo handle it if it can. Honestly idk why this is necessary though...

            if (args?.where?.id && Object.keys(args.where).length === 1 && !args.options) {

                const refs = [toReference({
                    __typename: curr[1],
                    id: args.where.id,
                })];

                if (!refs.every(canRead)) {
                    return existing;
                }

                return refs;
            }
            if (args?.where?.id_IN && Object.keys(args.where).length === 1 && !args.options) {

                const refs: (Reference | undefined)[] = args.where.id_IN.map((id: string) => toReference({
                    __typename: curr[1],
                    id,
                }));

                if (refs.some(ref => !canRead(ref))) {
                    return existing;
                }

                return refs;
            }
            return existing;
        },
    }
    return acc;
}, {} as Record<string, FieldPolicy>);

export default function ClientProvider(props: Props) {
    const getToken = useGetToken();

    const cache = useMemo(() =>
        new InMemoryCache({
            possibleTypes: {
                EpisodeContent: ['Line', 'LineGroup', 'Song', 'Audio', 'AudioGroup'],
                ScriptContent: ['Line', 'LineGroup', 'Song', 'Audio', 'AudioGroup'],
                SongContent: ['Line', 'LineGroup'],
            },
            typePolicies: {
                Query: {
                    fields,
                },
            },
        }), []);

    const httpLink = useMemo(() => createHttpLink({
        uri: '/graphql',
    }), []);

    const authLink = useMemo(() => setContext(async (_, { headers }) => {
        const token = await getToken();
        if (!token) return;

        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : '',
            },
        }
    }), [getToken]);

    const newClient = useMemo(() => new ApolloClient({
        link: authLink.concat(httpLink),
        cache,
        connectToDevTools: true,
    }), [authLink, httpLink, cache]);

    window.apolloClient = newClient;

    return (
        <ApolloProvider client={newClient}>
            {props.children}
        </ApolloProvider>
    )
}